import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, MenuItem, IconButton, Tooltip, Alert, Chip, Divider,
} from '@mui/material';
import { Home, Assignment, Print } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const Stat = ({ value, label, color }) => (
  <Card elevation={2} sx={{ borderRadius: 3, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ py: 1.8 }}>
      <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
      <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
    </CardContent>
  </Card>
);

const AppraisalReports = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod]   = useState('');
  const [summary, setSummary] = useState(null);
  const [empNo, setEmpNo]     = useState('');
  const [history, setHistory] = useState([]);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/appraisal/periods`, { headers: hdrs() }).then(r => setPeriods(r.data)).catch(() => {});
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const q = period ? `?periodCode=${period}` : '';
      const r = await axios.get(`${API}/api/appraisal/reports/summary${q}`, { headers: hdrs() });
      setSummary(r.data); setError('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load summary'); }
  }, [period]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadHistory = async () => {
    if (!empNo) return;
    try { const r = await axios.get(`${API}/api/appraisal/reports/history?employeeNo=${empNo}`, { headers: hdrs() }); setHistory(r.data); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1050, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1} className="no-print">
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment color="primary" />
              <Typography variant="h5" color="primary">Appraisal Reports</Typography>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <TextField select size="small" label="Period" value={period} onChange={e => setPeriod(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value="">All periods</MenuItem>
                {periods.map(p => <MenuItem key={p.code} value={p.code}>{p.code} — {p.name}</MenuItem>)}
              </TextField>
              <Tooltip title="Print"><IconButton onClick={() => window.print()}><Print /></IconButton></Tooltip>
              <Tooltip title="Appraisals"><IconButton onClick={() => navigate('/hr/appraisal')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <CompanyHeader title="Performance Appraisal Report" subtitle={period || undefined} />

          {/* Organization Performance Summary */}
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Organization Performance Summary {period && <Chip label={period} size="small" sx={{ ml: 1 }} />}</Typography>
          {summary && (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}><Stat value={summary.completed} label="Appraisals Completed" color="#1565c0" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={`${summary.avgScore}%`} label="Average Score" color="#00695c" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.metTarget} label="Met Target (≥60%)" color="#2e7d32" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.notMetTarget} label="Did Not Meet" color="#e65100" /></Grid>
              </Grid>
              {summary.onPIP > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{summary.onPIP} staff placed on Performance Improvement Plan.</Alert>}

              <Typography variant="subtitle2" fontWeight={700} mb={1}>Performance by Department</Typography>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="center"><strong>Appraisals</strong></TableCell>
                  <TableCell align="center"><strong>Avg Score</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(summary.byDepartment || []).length === 0 ? (
                    <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary" py={2}>No data.</Typography></TableCell></TableRow>
                  ) : summary.byDepartment.map(d => (
                    <TableRow key={d.department} hover>
                      <TableCell>{d.department}</TableCell>
                      <TableCell align="center">{d.count}</TableCell>
                      <TableCell align="center"><Chip label={`${d.avg}%`} size="small" color={d.avg >= 60 ? 'success' : 'warning'} variant="outlined" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Performance history per employee */}
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Performance History per Employee</Typography>
          <Box display="flex" gap={1.5} mb={2} className="no-print">
            <TextField size="small" label="Employee No" value={empNo} onChange={e => setEmpNo(e.target.value)} sx={{ minWidth: 180 }}
              onKeyDown={e => e.key === 'Enter' && loadHistory()} />
            <IconButton onClick={loadHistory} color="primary"><Assignment /></IconButton>
          </Box>
          {history.length > 0 && (
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Period</strong></TableCell>
                <TableCell><strong>Appraisal No</strong></TableCell>
                <TableCell align="center"><strong>Score</strong></TableCell>
                <TableCell><strong>Rating</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {history.map(h => (
                  <TableRow key={h._id} hover>
                    <TableCell>{h.periodCode}</TableCell>
                    <TableCell>{h.appraisalNo}</TableCell>
                    <TableCell align="center">{h.finalScore != null ? `${h.finalScore}%` : '—'}</TableCell>
                    <TableCell>{h.finalRating || '—'}</TableCell>
                    <TableCell><Chip label={h.status} size="small" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppraisalReports;
